import CircleIcon from "@mui/icons-material/Circle"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputLabel from "@mui/material/InputLabel"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { DatePicker, TimePicker } from "@mui/x-date-pickers"
import {
  addBusinessDays,
  addDays,
  addMonths,
  addWeeks,
  format as formatDate,
  getDate,
  getHours,
  getMinutes,
  getMonth,
  getYear,
  setDate,
  setHours,
  setMinutes,
  setMonth,
  setSeconds,
  setYear,
  subDays,
  subHours,
  subMinutes,
  subWeeks,
} from "date-fns"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import { useCloseableSnackbarState } from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import { TASK_PRIORITIES, TASK_PRIORITY_NONE, TASK_TYPES } from "../tasks/utils"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"
import SelectCandidateAutocomplete, {
  useSelectCandidateAutocompleteState,
} from "./SelectCandidateAutocomplete"
import SelectSMSCampaignAutoComplete, {
  useSelectSMSCampaignAutoCompleteState,
} from "./SelectSMSCampaignAutoComplete"

export default function TaskDialog({
  open,
  onClose,
  task,
  candidate,
  workflowMode = false,
}) {
  const [title, setTitle] = useState("")
  const [type, setType] = useState("")
  const [priority, setPriority] = useState(TASK_PRIORITY_NONE)
  const {
    searchText,
    setSearchText,
    selectedCandidate,
    setSelectedCandidate,
    clearSelectCandidateAutocomplete,
  } = useSelectCandidateAutocompleteState()
  const [dueDateSelect, setDueDateSelect] = useState(DEFAULT_DUE_DATE_KEY)
  const [dueDate, setDueDate] = useState(DEFAULT_DUE_DATE_OPTION.dueDate)
  const [reminderDateSelect, setReminderDateSelect] = useState(
    DEFAULT_REMINDER_DATE_KEY
  )
  const [reminderDate, setReminderDate] = useState(null)
  const [notes, setNotes] = useState("")
  const [completed, setCompleted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const {
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    availableCampaigns,
    availableCampaignsOptions,
    campaignsLoaded,
  } = useSelectSMSCampaignAutoCompleteState(open)
  const axios = useAuthorizedAxiosClient()
  const isMobile = useIsMobile()
  const Container = useDialogContainer()
  const DATE_PICKER_WIDTH = { flexBasis: "60%" }
  const COMMON_DATE_PICKER_PROPS = {
    disablePast: true,
    slotProps: { actionBar: { actions: ["today", "clear"] } },
    sx: DATE_PICKER_WIDTH,
  }
  const TASK_API_URL = `/crm/api/tasks/${task?.id}/`
  const editingTaskAndNotWorkflowMode = task && !workflowMode

  function handleSoftClose() {
    onClose()
  }

  function handleDueDateSelectChanged(optionKey) {
    const dueDateOption = DUE_DATE_OPTIONS.find(
      (option) => option.key === optionKey
    )
    setDueDateSelect(dueDateOption.key)
    if (optionKey === DUE_DATE_OPTION_KEYS.custom) {
      updateDueDateFromDate(new Date())
    } else {
      updateDueDateFromDate(dueDateOption.dueDate)
    }
  }

  function handleDueDateChanged(newDate) {
    if (newDate) {
      updateDueDateFromDate(newDate)
    } else {
      // cleared
      handleDueDateSelectChanged(DEFAULT_DUE_DATE_KEY)
    }
  }

  function handleDueTimeChanged(newDate) {
    updateTimePortionFromDate(newDate, dueDate, setDueDate)
  }

  function updateDueDateFromDate(date) {
    updateDatePortionFromDate(date, dueDate, setDueDate)
  }

  function handleReminderDateSelectChanged(optionKey) {
    setReminderDateSelect(optionKey)
    const reminderDateOption = REMINDER_DATE_OPTIONS.find(
      (option) => option.key === optionKey
    )
    setReminderDate(reminderDateOption.getReminderDate(dueDate))
  }

  function handleReminderDateChanged(newDate) {
    if (newDate) {
      updateDatePortionFromDate(newDate, reminderDate, setReminderDate)
    } else {
      // cleared
      handleReminderDateSelectChanged(DEFAULT_REMINDER_DATE_KEY)
    }
  }

  function handleReminderTimeChanged(newDate) {
    updateTimePortionFromDate(newDate, reminderDate, setReminderDate)
  }

  function updateDatePortionFromDate(date, targetDate, setTargetDate) {
    const yearsSet = setYear(targetDate, getYear(date))
    const monthsSet = setMonth(yearsSet, getMonth(date))
    const dateSet = setDate(monthsSet, getDate(date))
    setTargetDate(dateSet)
  }

  function updateTimePortionFromDate(date, targetDate, setTargetDate) {
    const hoursSet = setHours(targetDate, getHours(date))
    const minutesSet = setMinutes(hoursSet, getMinutes(date))
    setTargetDate(minutesSet)
  }

  function handlePrimaryButtonClicked() {
    if (workflowMode) {
      onClose({
        title,
        type,
        priority,
        notes,
        sms_campaign: selectedCampaign?.id,
      })
    } else {
      setIsLoading(true)
      const apiData = {
        title,
        type,
        priority,
        candidate_write: selectedCandidate.id,
        due_datetime: dueDate.toISOString(),
        reminder_datetime: reminderDate?.toISOString(),
        notes,
        completed,
      }
      let apiPromise
      if (task) {
        apiPromise = axios.put(TASK_API_URL, apiData)
      } else {
        apiPromise = axios.post("/crm/api/tasks/", apiData)
      }
      apiPromise
        .then(({ data }) => {
          onClose(data, TASK_ACTIONS.edited)
          resetFieldsToDefaults()
        })
        .finally(() => setIsLoading(false))
    }
  }

  function handleDeleteButtonClicked() {
    setIsDeleting(true)
    axios
      .delete(TASK_API_URL)
      .then(() => {
        onClose(task, TASK_ACTIONS.deleted)
        resetFieldsToDefaults()
      })
      .finally(() => setIsDeleting(false))
  }

  function resetFieldsToDefaults() {
    updateFields(
      "",
      "",
      TASK_PRIORITY_NONE,
      candidate,
      DEFAULT_DUE_DATE_KEY,
      DEFAULT_DUE_DATE_OPTION.dueDate,
      DEFAULT_REMINDER_DATE_KEY,
      null,
      "",
      false
    )
  }

  function updateFields(
    title,
    type,
    priority,
    candidate,
    dueDateSelect,
    dueDate,
    reminderDateSelect,
    reminderDate,
    notes,
    completed,
    smsCampaignID
  ) {
    setTitle(title || "")
    setType(type)
    setPriority(priority)
    updateCandidateFields(candidate)
    setDueDateSelect(dueDateSelect)
    setDueDate(dueDate)
    setReminderDateSelect(reminderDateSelect)
    setReminderDate(reminderDate)
    setNotes(notes || "")
    setCompleted(completed)
    if (workflowMode) {
      if (smsCampaignID) {
        if (campaignsLoaded) {
          const campaign = availableCampaignsOptions.filter(
            ({ id }) => id === smsCampaignID
          )[0]
          setCampaignInputValue(campaign.name)
          setSelectedCampaign(campaign)
        }
      } else {
        setSelectedCampaign(null)
      }
    }
  }

  function updateCandidateFields(candidate) {
    if (candidate) {
      setSearchText(candidate.candidate_name)
      setSelectedCandidate(candidate)
    } else {
      clearSelectCandidateAutocomplete()
    }
  }

  useEffect(
    () => {
      if (task) {
        const hasReminderDate = !!task.reminder_datetime
        updateFields(
          task.title,
          task.type,
          task.priority,
          task.candidate,
          CUSTOM_DUE_DATE_OPTION.key,
          new Date(task.due_datetime),
          hasReminderDate
            ? CUSTOM_REMINDER_DATE_OPTION.key
            : DEFAULT_REMINDER_DATE_KEY,
          hasReminderDate ? new Date(task.reminder_datetime) : null,
          task.notes,
          task.completed,
          task.sms_campaign
        )
      } else {
        resetFieldsToDefaults()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task, campaignsLoaded]
  )

  useEffect(
    () => {
      updateCandidateFields(candidate)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate]
  )

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
    >
      <DialogHeader
        title={`${workflowMode ? "Configure" : task ? "Edit" : "Add"} Task`}
        onClose={handleSoftClose}
      />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <LengthLimitedTextField
            label="Title"
            value={title}
            setValue={setTitle}
            maxLength={128}
            required
          />
          <DateTimeRow>
            <FormControl size="small" fullWidth>
              <InputLabel id="task-type" required>
                Type
              </InputLabel>
              <Select
                labelId="task-type"
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {Object.entries(TASK_TYPES).map(([typeValue, typeDisplay]) => (
                  <MenuItem value={typeValue} key={typeValue}>
                    {typeDisplay}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="task-priority">Priority</InputLabel>
              <Select
                labelId="task-priority"
                label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                renderValue={(value) => <ColorCodedPriority priority={value} />}
              >
                {Object.entries(TASK_PRIORITIES).map(
                  ([priority, priorityDisplay]) => (
                    <MenuItem key={priority} value={priority}>
                      <ListItemIcon>
                        <PriorityCircleIcon priority={priorityDisplay} />
                      </ListItemIcon>
                      <ListItemText>{priorityDisplay}</ListItemText>
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </DateTimeRow>
          {workflowMode ? (
            <SelectSMSCampaignAutoComplete
              availableCampaigns={availableCampaigns}
              availableCampaignsOptions={availableCampaignsOptions}
              campaignInputValue={campaignInputValue}
              setCampaignInputValue={setCampaignInputValue}
              selectedCampaign={selectedCampaign}
              setSelectedCampaign={setSelectedCampaign}
              campaignsLoaded={campaignsLoaded}
            />
          ) : (
            <>
              <SelectCandidateAutocomplete
                searchText={searchText}
                setSearchText={setSearchText}
                selectedCandidate={selectedCandidate}
                setSelectedCandidate={setSelectedCandidate}
                autoCompleteProps={{
                  disabled: !!candidate,
                }}
                textFieldProps={{
                  required: true,
                }}
              />
              <DateTimeRow>
                {dueDateSelect === DUE_DATE_OPTION_KEYS.custom ? (
                  <DatePicker
                    label="Due date"
                    value={dueDate}
                    onChange={handleDueDateChanged}
                    {...COMMON_DATE_PICKER_PROPS}
                  />
                ) : (
                  <FormControl size="small" sx={DATE_PICKER_WIDTH}>
                    <InputLabel id="due-date" required>
                      Due date
                    </InputLabel>
                    <Select
                      labelId="due-date"
                      label="Due date"
                      value={dueDateSelect}
                      onChange={(e) =>
                        handleDueDateSelectChanged(e.target.value)
                      }
                    >
                      {DUE_DATE_OPTIONS.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TimePicker
                  label="Time"
                  value={dueDate}
                  onChange={handleDueTimeChanged}
                />
              </DateTimeRow>
              <DateTimeRow>
                {reminderDateSelect === REMINDER_DATE_OPTION_KEYS.custom ? (
                  <>
                    <DatePicker
                      label="Reminder date"
                      value={reminderDate}
                      onChange={handleReminderDateChanged}
                      {...COMMON_DATE_PICKER_PROPS}
                    />
                    <TimePicker
                      label="Time"
                      value={reminderDate}
                      onChange={handleReminderTimeChanged}
                    />
                  </>
                ) : (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="reminder-date">Reminder</InputLabel>
                    <Select
                      label="Reminder"
                      labelId="reminder-date"
                      value={reminderDateSelect}
                      onChange={(e) =>
                        handleReminderDateSelectChanged(e.target.value)
                      }
                    >
                      {REMINDER_DATE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.key}
                          value={option.key}
                          disabled={option.getIsDisabled(dueDate)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </DateTimeRow>
            </>
          )}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
          />
          {editingTaskAndNotWorkflowMode && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={completed}
                  onChange={(e) => setCompleted(e.target.checked)}
                  disableRipple
                  sx={{ p: 0 }}
                />
              }
              label="Task complete"
              sx={{ ml: 0, gap: 1 }}
            />
          )}
        </Column>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: editingTaskAndNotWorkflowMode
            ? "space-between"
            : "flex-end",
        }}
      >
        {editingTaskAndNotWorkflowMode && (
          <LoadingButton
            variant="contained"
            color="error"
            loading={isDeleting}
            onClick={handleDeleteButtonClicked}
          >
            Delete
          </LoadingButton>
        )}
        <Row sx={{ gap: 1 }}>
          <Button variant="outlined" color="primary" onClick={handleSoftClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={
              !title ||
              !type ||
              !priority ||
              (!workflowMode && (!selectedCandidate || !dueDate))
            }
            loading={isLoading}
            onClick={handlePrimaryButtonClicked}
          >
            {task || workflowMode ? "Save" : "Add task"}
          </LoadingButton>
        </Row>
      </DialogActions>
    </Container>
  )
}

export function ColorCodedPriority({ priority }) {
  return (
    <Row sx={{ gap: 1, alignItems: "center" }}>
      <PriorityCircleIcon priority={TASK_PRIORITIES[priority]} />
      <Typography variant="body1" sx={{ fontSize: "1rem" }}>
        {TASK_PRIORITIES[priority]}
      </Typography>
    </Row>
  )
}

export const PriorityCircleIcon = styled(CircleIcon, {
  shouldForwardProp: (prop) => prop !== "priority",
})(({ theme, priority }) => ({
  fill: getPriorityCircleColor(priority, theme),
}))

const DateTimeRow = styled(Row)(({ theme }) => ({
  gap: theme.spacing(1),
}))

class DueDateOption {
  static dayFormat = "MMMM d"

  constructor(key, period, periodLength) {
    this.key = key
    this.period = period
    this.periodLength = periodLength
  }

  get label() {
    switch (this.period) {
      case DUE_DATE_PERIODS.day:
        switch (this.periodLength) {
          case 0:
            return "Today"
          case 1:
            return "Tomorrow"
          default:
            return ""
        }
      case DUE_DATE_PERIODS.bizDay:
        return this.getBusinessDayLabel()
      case DUE_DATE_PERIODS.week:
        return this.getWeeksLabel()
      case DUE_DATE_PERIODS.month:
        return this.getMonthsLabel()
      default:
        return "Custom date"
    }
  }

  get dueDate() {
    switch (this.period) {
      case DUE_DATE_PERIODS.day:
        return addDays(getToday8AM(), this.periodLength)
      case DUE_DATE_PERIODS.bizDay:
        return this.getBusinessDayDate()
      case DUE_DATE_PERIODS.week:
        return this.getWeeksDate()
      case DUE_DATE_PERIODS.month:
        return this.getMonthsDate()
      default:
        return "Custom date"
    }
  }

  getBusinessDayLabel() {
    return this.getLabel(this.getBusinessDayDate(), "EEEE", "business day")
  }

  getBusinessDayDate() {
    return addBusinessDays(getToday8AM(), this.periodLength)
  }

  getWeeksLabel() {
    return this.getLabel(this.getWeeksDate(), DueDateOption.dayFormat, "week")
  }

  getWeeksDate() {
    return addWeeks(getToday8AM(), this.periodLength)
  }

  getMonthsLabel() {
    return this.getLabel(this.getMonthsDate(), DueDateOption.dayFormat, "month")
  }

  getMonthsDate() {
    return addMonths(getToday8AM(), this.periodLength)
  }

  getLabel(futureDate, suffixFormat, periodLabel) {
    return `In ${this.periodLength} ${periodLabel}${
      this.periodLength > 1 ? "s" : ""
    } (${formatDate(futureDate, suffixFormat)})`
  }
}

function getBalancedObject(keys) {
  return Object.fromEntries(keys.map((key) => [key, key]))
}

const DUE_DATE_OPTION_KEYS = getBalancedObject([
  "today",
  "tomorrow",
  "oneBizDay",
  "twoBizDays",
  "threeBizDays",
  "oneWeek",
  "twoWeeks",
  "oneMonth",
  "threeMonths",
  "sixMonths",
  "custom",
])

const DEFAULT_DUE_DATE_KEY = DUE_DATE_OPTION_KEYS.threeBizDays

const DUE_DATE_PERIODS = getBalancedObject(["day", "bizDay", "week", "month"])

const DEFAULT_DUE_DATE_OPTION = new DueDateOption(
  DUE_DATE_OPTION_KEYS.threeBizDays,
  DUE_DATE_PERIODS.bizDay,
  3
)

const CUSTOM_DUE_DATE_OPTION = new DueDateOption(DUE_DATE_OPTION_KEYS.custom)

const DUE_DATE_OPTIONS = [
  new DueDateOption(DUE_DATE_OPTION_KEYS.today, DUE_DATE_PERIODS.day, 0),
  new DueDateOption(DUE_DATE_OPTION_KEYS.tomorrow, DUE_DATE_PERIODS.day, 1),
  new DueDateOption(DUE_DATE_OPTION_KEYS.oneBizDay, DUE_DATE_PERIODS.bizDay, 1),
  new DueDateOption(
    DUE_DATE_OPTION_KEYS.twoBizDays,
    DUE_DATE_PERIODS.bizDay,
    2
  ),
  DEFAULT_DUE_DATE_OPTION,
  new DueDateOption(DUE_DATE_OPTION_KEYS.oneWeek, DUE_DATE_PERIODS.week, 1),
  new DueDateOption(DUE_DATE_OPTION_KEYS.twoWeeks, DUE_DATE_PERIODS.week, 2),
  new DueDateOption(DUE_DATE_OPTION_KEYS.oneMonth, DUE_DATE_PERIODS.month, 1),
  new DueDateOption(
    DUE_DATE_OPTION_KEYS.threeMonths,
    DUE_DATE_PERIODS.month,
    3
  ),
  new DueDateOption(DUE_DATE_OPTION_KEYS.sixMonths, DUE_DATE_PERIODS.month, 6),
  CUSTOM_DUE_DATE_OPTION,
]

const REMINDER_DATE_OPTION_KEYS = getBalancedObject([
  "noReminder",
  "atDueTime",
  "thirtyMins",
  "hourBefore",
  "dayBefore",
  "weekBefore",
  "custom",
])

const DEFAULT_REMINDER_DATE_KEY = REMINDER_DATE_OPTION_KEYS.noReminder

const REMINDER_DATE_PERIODS = getBalancedObject(["min", "hour", "day", "week"])

class ReminderDateOption {
  constructor(key, label, period, periodLength) {
    this.key = key
    this.label = label
    this.period = period
    this.periodLength = periodLength
  }

  getReminderDate(dueDate) {
    switch (this.key) {
      case REMINDER_DATE_OPTION_KEYS.noReminder:
        return null
      case REMINDER_DATE_OPTION_KEYS.atDueTime:
        return dueDate
      case REMINDER_DATE_OPTION_KEYS.custom:
        return getToday8AM()
      default:
        switch (this.period) {
          case REMINDER_DATE_PERIODS.min:
            return subMinutes(dueDate, this.periodLength)
          case REMINDER_DATE_PERIODS.hour:
            return subHours(dueDate, this.periodLength)
          case REMINDER_DATE_PERIODS.day:
            return subDays(dueDate, this.periodLength)
          default:
            return subWeeks(dueDate, this.periodLength)
        }
    }
  }

  getIsDisabled(dueDate) {
    // please choose a reminder in the future
    switch (this.key) {
      case REMINDER_DATE_OPTION_KEYS.noReminder:
      case REMINDER_DATE_OPTION_KEYS.atDueTime:
      case REMINDER_DATE_OPTION_KEYS.custom:
        return false
      default:
        return this.getReminderDate(dueDate) < new Date()
    }
  }
}

const CUSTOM_REMINDER_DATE_OPTION = new ReminderDateOption(
  REMINDER_DATE_OPTION_KEYS.custom,
  "Custom date"
)

const REMINDER_DATE_OPTIONS = [
  new ReminderDateOption(REMINDER_DATE_OPTION_KEYS.noReminder, "No reminder"),
  new ReminderDateOption(
    REMINDER_DATE_OPTION_KEYS.atDueTime,
    "At task due time"
  ),
  new ReminderDateOption(
    REMINDER_DATE_OPTION_KEYS.thirtyMins,
    "30 minutes before",
    REMINDER_DATE_PERIODS.min,
    30
  ),
  new ReminderDateOption(
    REMINDER_DATE_OPTION_KEYS.hourBefore,
    "1 hour before",
    REMINDER_DATE_PERIODS.hour,
    1
  ),
  new ReminderDateOption(
    REMINDER_DATE_OPTION_KEYS.dayBefore,
    "1 day before",
    REMINDER_DATE_PERIODS.day,
    1
  ),
  new ReminderDateOption(
    REMINDER_DATE_OPTION_KEYS.weekBefore,
    "1 week before",
    REMINDER_DATE_PERIODS.week,
    1
  ),
  CUSTOM_REMINDER_DATE_OPTION,
]

function getToday8AM() {
  return setHours(setMinutes(setSeconds(new Date(), 0), 0), 8)
}

export const TASK_ACTIONS = {
  edited: 1,
  deleted: 2,
}

export function useTaskDialogState() {
  const [tasks, setTasks] = useState([])
  const [clickedTask, setClickedTask] = useState(null)
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const {
    isOpen: isSnackbarOpen,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
  } = useCloseableSnackbarState()

  function openTaskDialog() {
    setTaskDialogOpen(true)
  }

  function handleTaskDialogClosed(newOrUpdatedTask, taskAction) {
    if (newOrUpdatedTask) {
      let message
      if (clickedTask) {
        if (taskAction === TASK_ACTIONS.edited) {
          setTasks(
            tasks.map((task_) =>
              task_.id === clickedTask.id ? newOrUpdatedTask : task_
            )
          )
          message = "Task updated"
        } else {
          setTasks(tasks.filter((task) => task.id !== clickedTask.id))
          message = "Task deleted"
        }
      } else {
        setTasks([newOrUpdatedTask, ...tasks])
        message = "Task created successfully"
      }
      openSnackbar(message)
    }
    setTaskDialogOpen(false)
    setClickedTask(null)
  }

  return {
    tasks,
    setTasks,
    clickedTask,
    setClickedTask,
    taskDialogOpen,
    openTaskDialog,
    handleTaskDialogClosed,
    isSnackbarOpen,
    openSnackbar,
    closeSnackbar,
    snackbarMessage,
  }
}

function getPriorityCircleColor(priority, theme) {
  let iconColor
  switch (priority) {
    case TASK_PRIORITIES.none:
      iconColor = theme.palette.grey[100]
      break
    case TASK_PRIORITIES.low:
      iconColor = theme.palette.success.light
      break
    case TASK_PRIORITIES.medium:
      iconColor = theme.palette.warning.light
      break
    default:
      iconColor = theme.palette.error.light
  }
  return iconColor
}
